<template>
  <div class="root">
    <div>
      <label>Source:</label>
      <Multiselect v-model="localItem.source" :disabled="isBusy" :options="contentSources" @change="onChange" />
    </div>
    <div>
      <div v-if="localItem.source === 'file'" class="file-source">
        <FileSelect accept=".docx,.vsdx,.png,.jpeg,.jpg,.svg" :multiple="true" variant="contained" @change="fileSelected">
          <MdIcon size="md" name="file-word" style="margin-right: 3px" />Select File(s)
        </FileSelect>
        <div class="preview">
          <div v-for="file in localItem.value" :key="file.name">
            <div class="file">
              <div class="thumbnail">
                {{ file.name }}
              </div>
              <div class="actions">
                <Button class="button" title="Download" type="button" @click="download(file)">
                  <MdIcon size="md" name="download" />
                </Button>
              <Button class="button" title="Remove" @click="clear(file)">
                  <MdIcon size="md" name="close" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="localItem.source === 'mermaid'" class="mermaid-source">
        <label>Template:</label>
        <Code
          v-model="localItem.value.template"
          :read-only="!$hasPermission('prompts.write') || isBusy"
          :language="'plaintext'"
          class="editor"
          :suggestions="suggestions"
          :auto-height="true"
          @input="onChange"
        />
      </div>
      <div v-else>
        <div>
          <Multiselect
            v-model="localItem.value.model"
            label="Model"
            :searchable="false"
            placeholder="Select a model (optional)"
            :options="allowedModels"
            :disabled="isBusy"
            :get-label-callback="id => getLabel(id)"
            @input="onChange"
          />
        </div>
        <div>
          <label>Prompt:</label>
          <Code
            v-model="localItem.value.template"
            :read-only="!$hasPermission('prompts.write') || isBusy"
            :language="'plaintext'"
            class="editor"
            :suggestions="suggestions"
            :auto-height="true"
            @input="onChange"
          />
        </div>
        <div>
          <label>Image to edit:</label>
          <Multiselect v-model="localItem.value.images" :searchable="false" :disabled="isBusy" :options="imageVariables" @change="onChange" />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import Multiselect from '@/components/common/Multiselect';
import FileSelect from '@/components/common/FileSelect';
import Button from '@/components/common/Button';
import MdIcon from '@/components/common/MdIcon';
import Code from '@/components/common/Code';
import httpClient from '@/utils/httpClient';

export default {
  components: {
    Multiselect,
    FileSelect,
    Button,
    MdIcon,
    Code
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    template: {
      type: Object,
      required: true
    },
    suggestions: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      localItem: {
        ...this.item
      }
    };
  },
  computed: {
    ...mapState({
      contentSources: s => s.nlp.contentSources,
      modelPermissions: state => state.nlp.modelPermissions ?? []
    }),
    ...mapGetters({
      models: 'nlp/imageModels',
      isBusy: 'prompts/isBusy'
    }),
    imageVariables() {
      const forms = this.template.template.chain.filter(l => l.type === 'form');
      const imageVariables = forms
        .map(f => f.variables.filter(v => v.type === 'image'))
        .flat()
        .map(v => v.name);

      return ['', ...imageVariables];
    },
    allowedModels() {
      const imageModels = this.models;
      if (this.template.isGeneric) {
        return ['', ...imageModels.map(m => m.id)];
      }

      const organizations = this.template?.organizations ?? [];

      if (!organizations.length) {
        return [''];
      }

      const allowedModels = [];

      const organizationsPermisssions = this.modelPermissions.filter(mp => organizations.includes(mp.organization));

      for (const model of new Set(organizationsPermisssions.map(mp => mp.model))) {
        if (organizationsPermisssions.filter(op => op.model === model).length === organizations.length && imageModels.some(m => m.id === model)) {
          allowedModels.push(model);
        }
      }

      return ['', ...allowedModels];
    }
  },
  watch: {
    'localItem.source': {
      handler(newValue, oldValue) {
        switch (newValue) {
          case 'mermaid':
          case 'ai':
            this.localItem.value = {
              template: ''
            };
            break;
          case 'file':
            this.localItem.value = [];
            break;
        }
      },
      deep: true
    }
  },
  methods: {
    onChange() {
      this.$emit('change', { ...this.localItem });
    },
    async download(file) {
      if (!file) {
        return;
      }

      if (typeof file.base64 === 'string') {
        return this.downloadBase64(file.base64, file.type, file.name);
      }

      if (!file.id) {
        this.$toast?.error({
          title: 'Download failed',
          message: 'File content is not available.'
        });
        return;
      }

      try {
        const { base64, name, type } = await httpClient.get(`/api/prompts/content/${file.id}/download`);
        if (!base64) {
          throw new Error('Missing file content');
        }
        this.downloadBase64(base64, type || file.type, name || file.name || 'download');
      } catch (error) {
        this.$toast?.error({
          title: 'Download failed',
          message: 'File content is not available.'
        });
      }
    },
    downloadBase64(base64, type, name) {
      if (!base64) {
        return;
      }

      const mimeType = type || 'application/octet-stream';
      const dataUrl = base64.startsWith('data:') ? base64 : `data:${mimeType};base64,${base64}`;
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = this.normalizeFileName(name) ?? 'download';
      link.click();
    },
    normalizeFileName(name) {
      if (!name || typeof name !== 'string') {
        return name;
      }

      return name.replace(/["']/g, '').trim();
    },
    getLabel(id) {
      return this.models.find(m => m.id === id)?.name ?? id;
    },
    async fileSelected(files) {
      const loadedFiles = [];
      for (const file of files) {
        if (/(docx)|(vsdx)|(jpg)|(jpeg)|(png)|(svg)$/.test(file.name)) {
          this.progress = `Loading ${file.name}...`;
          const loadedFile = await new Promise(async function(resolve, reject) {
            var reader = new FileReader();
            reader.onload = () => {
              resolve({
                name: file.name,
                type: reader.result.match(/data:(.*?);base64/)[1],
                base64: reader.result
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          loadedFiles.push(loadedFile);
        }
      }

      this.localItem.value = loadedFiles;

      this.onChange();
    },
    clear(file) {
      if (Array.isArray(this.localItem.value)) {
        const index = this.localItem.value.indexOf(file);
        this.localItem.value.splice(index, 1);
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.root {
  display: grid;
  gap: 10px;
  grid-template-rows: auto auto 1fr;

  label {
    font-weight: 500;
    font-size: 0.8rem;
    letter-spacing: 0.025em;
  }

  .mermaid-source {
    display: grid;
    gap: 10px;
  }

.file-source {
    display: grid;
    grid-template-rows: auto 1fr;
    gap: 10px;
    border: 1px solid var(--theme-on-background-dark);
    border-radius: 2px;
    padding: 2px;

    .file {
      display: flex;
      justify-content: flex-start;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px;

      div:not(:last-child) {
        margin-bottom: 1px;
      }

      .thumbnail {
        z-index: 0;
        padding: 2px;
        border-color: 1px white;

        img {
          max-height: 75px;
        }
      }

      .actions {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .file {
        display: flex;
        align-items: flex-start;
        position: relative;

        .button {
          visibility: hidden;
          right: 0;
          padding: 0;
          z-index: 1000;
          cursor: pointer;
          color: var(--theme-on-surface);
          border-radius: 2px;
        }
      }
      .file:hover .button {
        visibility: visible;
      }
      .file .button {
        visibility: visible;
      }
    }
  }
}
</style>
